import React, { FC } from 'react';
import { CareMapDrawerPage } from '../components/CareMapDrawerPage';
import { ISupportGroupKey } from '../caremap/ICareMap';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { DeleteIcon, EditIcon } from '../theme/Icons';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { Text } from '../components/Text';
import { Menu } from '../components/Menu';
import { useDeleteMember } from './useDeleteMember';
import { SupportGroupIconProvider } from '../caremap/SupportGroupIconProvider';
import { ContactMethodsList } from '../contact-methods/ContactMethodsList';
import { NameHelper } from '../contact-methods/NameHelper';
import { CareMapNotesList } from '../notes/note-list/CareMapNotesList';
import { NudgeBadge } from '../components/NudgeBadge';
import { DrawerListSection } from '../components/DrawerListSection';
import { AppointmentsList } from '../appointments/AppointmentsList';
import { AppointmentsState, NEW_APPOINTMENT_ID } from '../appointments/AppointmentsState';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';

export interface ISupportGroupMemberViewProps {
    supportGroupKey: ISupportGroupKey;
    memberId: string;
};

export const SupportGroupMemberView: FC<ISupportGroupMemberViewProps> = ({ supportGroupKey, memberId }) => {
    const nameHelper = useInstance(NameHelper);

    const history = useInstance(HistoryViewModel);

    const deleteMember = useDeleteMember({ supportGroupKey, memberId, onDelete: history.back });

    const careMapStateInstance = useInstance(CareMapState);
    useSubscription(() => careMapStateInstance);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const canEdit = careMapStateInstance.hasPermissionTo('put');

    const iconProvider = useInstance(SupportGroupIconProvider);
    const appointmentsState = useInstance(AppointmentsState);

    const member = careMapStateInstance.getSupportGroupMember({ supportGroupKey, memberId });

    if (!careMapStateInstance.state || !member) {
        return null;
    }

    const labels = careMapStateInstance.getSupportGroupLabels(supportGroupKey);
    const color = careMapStateInstance.getSupportGroupColor(supportGroupKey);
    const { MemberIcon } = iconProvider.getSupportGroupIcons(supportGroupKey);

    const editMember = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/member/${member.id}/edit`);
    };

    const viewMoreNotes = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/member/${memberId}/notes`);
    };

    const editNote = (noteId: string) => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/member/${memberId}/notes/${noteId}`);
    };

    const addNote = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/member/${memberId}/notes/add-note`);
    };

    const viewMoreAppointments = () => {
        history.push(`${history.getCurrentLocation().pathname}/appointments`);
    };

    const editAppointment = (appointmentId?: string) => {
        if (appointmentId === NEW_APPOINTMENT_ID) {
            appointmentsState.trackNewAppointmentOpened({ supportGroupKey, memberId });
        }

        history.push(`${history.getCurrentLocation().pathname}/appointments/${appointmentId}`);
    };

    const listShowCount = 2;
    const showNameFirst = careMapStateInstance.shouldShowNameFirst(supportGroupKey);
    const title = (showNameFirst ? member.name : member.role);
    const subtitle = (showNameFirst ? member.role : member.name);

    const needsNameOrRole = !member.name || ((supportGroupKey === 'financial' || supportGroupKey === 'community') && !member.role);
    const showEditNudgeBadge = canEdit && needsNameOrRole && !member.organization && !member.contactMethods.length && !member.address;

    const notes = notesFactory.forActiveCaremap().getNotesForTarget({ section: supportGroupKey, memberId });

    return (
        <CareMapDrawerPage title={`${labels.memberTitle} Detail`} hasBack={true}>

            <div data-type="support-group-member-view" style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>

                <div style={{ display: 'flex', alignItems: 'start', gap: 20 }}>
                    <div style={{width: 50}}>
                        <MemberIcon color={color} size="xlarge" shape="round" />
                    </div>

                    <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
                        <Text type="title" data-type="title">{title || ''}</Text>
                        <Text type="subtitle" data-type="subtitle">{subtitle || ''}</Text>
                        <Text type="detail" data-type="organization">{member.organization}</Text>
                    </div>

                    {canEdit &&
                        <div style={{marginTop: -8}}>
                            <Menu actions={[
                                { Icon: EditIcon, text: `Edit ${labels.memberTitle}`, handler: editMember },
                                { Icon: DeleteIcon, text: `Delete ${labels.memberTitle}`, role: 'destructive', handler: deleteMember }
                            ]} />
                        </div>
                    }
                </div>

                {showEditNudgeBadge &&
                    <NudgeBadge
                        buttonLabel="Edit"
                        buttonClickHandler={editMember}
                        message="Be sure to add address and contact info to help you stay organized"
                    />
                }

                <ContactMethodsList
                    contactMethods={member.contactMethods}
                    address={member.address}
                    iconWidth={50}
                    emailToName={nameHelper.getName({ firstName: member.name })}
                    trackingContext={{ category: supportGroupKey, role: member.roleId || member.role }}
                />

                <DrawerListSection>
                    <CareMapNotesList
                        showCount={listShowCount}
                        onNoteClicked={editNote}
                        onAddNote={addNote}
                        moreNotesHandler={viewMoreNotes}
                        notes={notes}
                    />
                </DrawerListSection>

                <DrawerListSection>
                    <AppointmentsList
                        showViewMore
                        numDaysToShow={1}
                        ignoreHasScrolled
                        showCount={listShowCount}
                        minDate={(new Date()).toISOString()}
                        supportMemberId={memberId}
                        editAppointment={editAppointment}
                        viewMore={viewMoreAppointments}
                        data-type="appointments-list"
                    />
                </DrawerListSection>
            </div>

        </CareMapDrawerPage>
    );
};
